/**
 * Derive the patient-tracker label from the clinic-customizable question.
 *
 * The default question is emotion-related ("How are you feeling today?"
 * etc.), but clinics can override the question for other tracking topics
 * such as bowel movements / digestive symptoms. We sniff the question
 * text and switch the label so the clinician UI matches what the patient
 * was actually being asked.
 *
 * Defaults to `'Emotion'` when no question is available or no keyword
 * matches.
 */
export type EmotionTopic = 'Emotion' | 'Digestion';

const DIGESTION_KEYWORDS = ['bowel movement', 'digestive symptom'];

export const emotionTopicFor = (question: string | undefined | null): EmotionTopic => {
  if (!question) return 'Emotion';
  const q = question.toLowerCase();
  if (DIGESTION_KEYWORDS.some((kw) => q.includes(kw))) return 'Digestion';
  return 'Emotion';
};

/**
 * Returns translation-key strings for each context so the consumer can
 * still go through `t()`. Using a topic-keyed lookup keeps the call site
 * readable: `t(LABELS[topic].trend)` rather than ternaries everywhere.
 */
export const TOPIC_LABELS: Record<EmotionTopic, {
  noun: string;          // single word, e.g. "Emotion"
  trend: string;         // chart title
  empty: string;         // empty-state title
  entriesCount: string;  // "{n} {entries}" — kept generic so reuse
}> = {
  Emotion: {
    noun: 'Emotion',
    trend: 'Emotion trend',
    empty: 'No emotion entries',
    entriesCount: 'entries',
  },
  Digestion: {
    noun: 'Digestion',
    trend: 'Digestion trend',
    empty: 'No digestion entries',
    entriesCount: 'entries',
  },
};
